import { Skeleton } from '@/components/ui/app-skeleton';
import { FEED_CARD_RESTING_OUTLINE } from '@/lib/ui/feed-card-chrome';
import { cn } from '@/lib/ui/utils';

import type { ProposalFeedCardVariant } from './proposal-feed-card';

function skeletonVisibilityClasses(variant: ProposalFeedCardVariant): string {
  switch (variant) {
    case 'mobile':
      return 'md:hidden flex flex-col gap-3';
    case 'tablet':
      return 'hidden md:flex lg:hidden flex-col gap-3 h-full min-h-0';
    case 'compact':
      return 'flex flex-col gap-3 h-full min-h-0';
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

export interface ProposalFeedCardSkeletonProps {
  variant: ProposalFeedCardVariant;
}

/** Matches `ProposalFeedCard` block structure for loading placeholders. */
export function ProposalFeedCardSkeleton({
  variant,
}: ProposalFeedCardSkeletonProps): React.ReactNode {
  return (
    <div
      className={cn(
        skeletonVisibilityClasses(variant),
        'rounded-card bg-background-primary px-4 py-4',
        FEED_CARD_RESTING_OUTLINE,
      )}
      aria-hidden="true"
    >
      <div className="flex items-start gap-3 min-w-0">
        <Skeleton
          className={cn(
            'size-12 shrink-0',
            variant === 'mobile' ? 'rounded-overlay-item' : 'rounded-control',
          )}
        />
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        <Skeleton className="h-5 w-16 shrink-0" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-1.5 w-full rounded-full" />
      </div>
      <div className="flex items-center gap-4 min-w-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Skeleton className="size-7 shrink-0 rounded-full" />
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex flex-col items-end gap-1">
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-3 w-12" />
          </div>
          <div className="flex items-center">
            <Skeleton className="size-7 shrink-0 rounded-full" />
            <Skeleton className="size-7 shrink-0 rounded-full -ml-3.5" />
            <Skeleton className="size-7 shrink-0 rounded-full -ml-3.5" />
          </div>
        </div>
      </div>
      <div className="h-[0.5px] w-full bg-border-primary my-1" aria-hidden="true" />
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/4" />
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Skeleton className="size-6 rounded-control" />
          <Skeleton className="h-4 w-8" />
          <Skeleton className="size-6 rounded-control" />
        </div>
      </div>
    </div>
  );
}
